/* eslint-disable react/jsx-props-no-spreading, react/jsx-no-bind */
import React, { useEffect, useRef, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import {
  CalculateOutlined,
  CheckBox,
  CheckBoxOutlineBlank,
  Close,
  LockOutlined,
  RefreshOutlined,
} from '@mui/icons-material';
import PropTypes from 'prop-types';
import _ from 'lodash';

import adminFormStyles from '../../shared/styles/adminForm.styles';
import colors from '../../colors.styles';
import CommonUtils from '../../shared/utils/common.utils';
import FormValidationsUtils from '../../shared/utils/formValidations.utils';
import {
  EMPLOYEE_TYPES,
  initTargeting,
  targetingToAPI,
} from '../TaskTargeting.utils';
import StoresApi from '../../stores/Stores.api';
import TasksApi from '../Tasks.api';

const DELIMITER = ',';
const { arrayToString, stringToArray } = CommonUtils;
const { allowedChars } = FormValidationsUtils;

const EMPLOYEE_TYPE_OPTIONS = EMPLOYEE_TYPES.map((value) => ({
  value,
  label: value === 'H' ? 'Hourly' : 'Salaried',
}));

const styles = {
  sectionTitle: adminFormStyles.sectionTitle,
  optionalLabel: adminFormStyles.optionalLabel,
  sectionDescription: adminFormStyles.sectionDescription,
  alert: {
    alignItems: 'flex-start',
    backgroundColor: '#fffbeb',
    border: '1px solid #fde68a',
    borderRadius: '12px',
    color: '#92400e',
    fontSize: '14px',
    py: 0.5,
    '& .MuiAlert-icon': { color: '#b45309', mt: 0.25 },
  },
  lockedAlert: {
    alignItems: 'flex-start',
    backgroundColor: '#f6f6f6',
    border: '1px solid #e9ecee',
    borderRadius: '12px',
    color: '#656d80',
    fontSize: '14px',
    py: 0.5,
    '& .MuiAlert-icon': { color: '#656d80', mt: 0.25 },
  },
  row: {
    borderBottom: '1px solid #e9ecee',
    minHeight: 64,
    py: 1.25,
    '&:last-of-type': { borderBottom: 'none' },
  },
  label: {
    color: colors.base.bentonville_blue,
    flex: '0 0 140px',
    fontSize: '14px',
    fontWeight: 700,
  },
  chip: {
    backgroundColor: colors.base.walmart_blue,
    borderRadius: '20px',
    color: colors.base.light,
    fontSize: '14px',
    fontWeight: 700,
    '& .MuiChip-deleteIcon': {
      color: colors.base.light,
      fontSize: '16px',
      '&:hover': { color: colors.base.light },
    },
    '&.MuiAutocomplete-tag': { margin: '0px 3px !important' },
  },
  // Autocomplete styled to read as a dashed "+ Add" pill while empty.
  filterInput: (isEmpty) => ({
    width: '100%',
    maxWidth: 420,
    ...adminFormStyles.field,
    '& .MuiOutlinedInput-root': {
      borderRadius: isEmpty ? '20px' : '8px',
    },
    '& .MuiOutlinedInput-notchedOutline': isEmpty
      ? { borderStyle: 'dashed', borderColor: '#a7b1c0' }
      : {},
    '& .MuiOutlinedInput-input::placeholder': {
      color: colors.base.walmart_blue,
      fontWeight: 700,
      opacity: 1,
    },
    '& .MuiOutlinedInput-input': { padding: '8px 14px' },
  }),
  textInput: {
    width: '100%',
    maxWidth: 240,
    ...adminFormStyles.field,
    '& .MuiOutlinedInput-input': { padding: '10px 14px' },
  },
  // First state: a plain full-width bordered button.
  calculateButton: {
    border: '1px solid #e9ecee',
    borderRadius: '12px',
    color: colors.base.walmart_blue,
    fontSize: '14px',
    fontWeight: 700,
    justifyContent: 'flex-start',
    minHeight: 48,
    px: 1.5,
    textTransform: 'none',
    width: '100%',
    '&:hover': {
      backgroundColor: colors.base.barely_blue,
      borderColor: '#dbe2e6',
    },
    '&.Mui-disabled': { color: colors.base.walmart_blue },
  },
  // After a calculation: a summary strip with a Recalculate action.
  reachBar: {
    alignItems: { xs: 'flex-start', sm: 'center' },
    backgroundColor: '#f6f6f6',
    border: '1px solid #e9ecee',
    borderRadius: '12px',
    gap: 1.5,
    justifyContent: 'space-between',
    px: 2,
    py: 1.5,
  },
  reachMetrics: {
    alignItems: 'center',
    color: colors.base.bentonville_blue,
    flexWrap: 'wrap',
    fontSize: '14px',
    gap: 1,
  },
  reachNumber: {
    fontSize: '20px',
    fontWeight: 800,
    lineHeight: 1,
  },
  reachMuted: { color: '#656d80' },
  reachDivider: {
    borderLeft: '1px solid #cfd6dd',
    height: 20,
    mx: 0.5,
  },
  recalcButton: {
    color: colors.base.walmart_blue,
    flexShrink: 0,
    fontSize: '14px',
    fontWeight: 700,
    textTransform: 'none',
  },
};

export default function TaskTargetingForm({
  initialTargeting,
  onStateChanged,
  isValidating,
  isReadOnly,
  savedReach,
}) {
  const [targeting, setTargeting] = useState(
    initialTargeting || initTargeting()
  );
  const [touched, setTouched] = useState({});
  const [validationErrors, setValidationErrors] = useState({});
  const [stateOptions, setStateOptions] = useState([]);
  const [storeTypeOptions, setStoreTypeOptions] = useState([]);

  const [reach, setReach] = useState(null);
  const [isReachLoading, setIsReachLoading] = useState(false);
  const [isReachStale, setIsReachStale] = useState(false);
  const [reachError, setReachError] = useState(null);

  const onStateChangedRef = useRef(onStateChanged);
  useEffect(() => {
    onStateChangedRef.current = onStateChanged;
  }, [onStateChanged]);

  useEffect(() => {
    let cancelled = false;

    async function fetchOptions() {
      try {
        const [storesResponse, storeTypes] = await Promise.all([
          StoresApi.getStores(),
          StoresApi.getStoreTypes(),
        ]);
        if (cancelled) return;
        setStateOptions([
          ...new Set(
            (storesResponse.data || [])
              .map(({ state }) => state)
              .filter(Boolean)
              .sort()
          ),
        ]);
        setStoreTypeOptions((storeTypes || []).filter(Boolean).sort());
      } catch (error) {
        // Non-fatal: leave the option lists empty.
        console.error('Error loading task targeting options:', error);
      }
    }

    fetchOptions();
    return () => {
      cancelled = true;
    };
  }, []);

  function validate(nextTargeting) {
    const newErrors = {};
    const onlyNums = allowedChars(`0-9 ${DELIMITER}`);

    ['regions', 'markets', 'store_numbers'].forEach((key) => {
      if (!onlyNums.isValid(nextTargeting[key].join(DELIMITER))) {
        newErrors[key] = onlyNums.helperText;
      }
    });

    setValidationErrors(newErrors);
    return _.isEmpty(newErrors);
  }

  useEffect(() => {
    onStateChangedRef.current(targeting, validate(targeting));
  }, [targeting, isValidating]);

  function updateTargeting(key, value) {
    setTouched((prev) => ({ ...prev, [key]: true }));
    setTargeting((prev) => ({ ...prev, [key]: value }));
    setIsReachStale((prev) => (reach ? true : prev));
  }

  function fieldError(key) {
    return (touched[key] || isValidating) && validationErrors[key];
  }

  async function calculateReach() {
    setIsReachLoading(true);
    setReachError(null);
    try {
      const result = await TasksApi.getReach(targetingToAPI(targeting));
      setReach(result);
      setIsReachStale(false);
    } catch (error) {
      console.error('Error calculating task reach:', error);
      setReachError('Could not calculate reach. Try again.');
    } finally {
      setIsReachLoading(false);
    }
  }

  function renderFilterRow(key, label, placeholder, options) {
    const value = targeting[key];
    return (
      <Stack
        key={key}
        direction={{ xs: 'column', sm: 'row' }}
        spacing={2}
        alignItems={{ sm: 'center' }}
        sx={styles.row}
      >
        <Typography sx={styles.label}>{label}</Typography>
        <Autocomplete
          multiple
          disableCloseOnSelect
          disabled={isReadOnly}
          options={options}
          value={value}
          onChange={(event, newValue) => updateTargeting(key, newValue)}
          sx={styles.filterInput(value.length === 0)}
          getOptionLabel={(option) =>
            typeof option === 'string' ? option : option.label
          }
          isOptionEqualToValue={(option, selected) =>
            (typeof option === 'string' ? option : option.value) ===
            (typeof selected === 'string' ? selected : selected.value)
          }
          renderInput={(params) => (
            <TextField
              {...params}
              placeholder={value.length ? '' : placeholder}
            />
          )}
          renderTags={(tagValues, getTagProps) =>
            tagValues.map((option, index) => {
              const { key: chipKey, ...chipProps } = getTagProps({ index });
              return (
                <Chip
                  key={chipKey}
                  label={typeof option === 'string' ? option : option.label}
                  deleteIcon={<Close />}
                  sx={styles.chip}
                  {...chipProps}
                />
              );
            })
          }
          renderOption={(props, option, { selected }) => {
            const SelectionIcon = selected ? CheckBox : CheckBoxOutlineBlank;
            return (
              <li {...props}>
                <SelectionIcon
                  fontSize="small"
                  style={{
                    marginRight: 8,
                    padding: 9,
                    boxSizing: 'content-box',
                  }}
                />
                {typeof option === 'string' ? option : option.label}
              </li>
            );
          }}
        />
      </Stack>
    );
  }

  function renderTextRow(key, label, placeholder) {
    return (
      <Stack
        key={key}
        direction={{ xs: 'column', sm: 'row' }}
        spacing={2}
        alignItems={{ sm: 'center' }}
        sx={styles.row}
      >
        <Typography sx={styles.label}>{label}</Typography>
        <TextField
          hiddenLabel
          disabled={isReadOnly}
          placeholder={placeholder}
          value={arrayToString(targeting[key], DELIMITER)}
          onChange={(event) =>
            updateTargeting(key, stringToArray(event.target.value, DELIMITER))
          }
          error={Boolean(fieldError(key))}
          helperText={fieldError(key)}
          sx={styles.textInput}
        />
      </Stack>
    );
  }

  function renderUsersRow() {
    return (
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        spacing={2}
        alignItems={{ sm: 'center' }}
        sx={styles.row}
      >
        <Typography sx={styles.label}>Users</Typography>
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          spacing={2}
          alignItems={{ sm: 'center' }}
          sx={{ flex: 1 }}
        >
          <Autocomplete
            multiple
            disabled={isReadOnly}
            options={EMPLOYEE_TYPE_OPTIONS}
            value={EMPLOYEE_TYPE_OPTIONS.filter((option) =>
              targeting.employee_types.includes(option.value)
            )}
            onChange={(event, newValue) =>
              // Pay type is single-select: keep only the most recent choice.
              updateTargeting(
                'employee_types',
                newValue.slice(-1).map((option) => option.value)
              )
            }
            getOptionLabel={(option) => option.label}
            isOptionEqualToValue={(option, selected) =>
              option.value === selected.value
            }
            sx={styles.filterInput(targeting.employee_types.length === 0)}
            renderInput={(params) => (
              <TextField
                {...params}
                placeholder={
                  targeting.employee_types.length ? '' : '+ Add pay type'
                }
              />
            )}
            renderTags={(tagValues, getTagProps) =>
              tagValues.map((option, index) => {
                const { key: chipKey, ...chipProps } = getTagProps({ index });
                return (
                  <Chip
                    key={chipKey}
                    label={option.label}
                    deleteIcon={<Close />}
                    sx={styles.chip}
                    {...chipProps}
                  />
                );
              })
            }
          />
          <TextField
            hiddenLabel
            disabled={isReadOnly}
            placeholder="Email, WIN, or user ID"
            value={arrayToString(targeting.specific_users, DELIMITER)}
            onChange={(event) =>
              updateTargeting(
                'specific_users',
                stringToArray(event.target.value, DELIMITER)
              )
            }
            sx={styles.textInput}
          />
        </Stack>
      </Stack>
    );
  }

  function renderReachSummary(value, { stale } = {}) {
    return (
      <>
        <Box component="span" sx={styles.reachNumber}>
          {value.store_count.toLocaleString()}
        </Box>
        <Box component="span" sx={styles.reachMuted}>
          {value.total_store_count != null
            ? `of ${value.total_store_count.toLocaleString()} stores targeted`
            : 'stores targeted'}
        </Box>
        <Box sx={styles.reachDivider} />
        <Box component="span" sx={styles.reachNumber}>
          {value.user_count.toLocaleString()}
        </Box>
        <Box component="span" sx={styles.reachMuted}>
          associates targeted
        </Box>
        {stale && (
          <Box component="span" sx={styles.reachMuted}>
            &nbsp;&mdash; out of date
          </Box>
        )}
      </>
    );
  }

  function renderReach() {
    // Live task: targeting is frozen, so show the reach captured at launch
    // rather than a "calculate" control.
    if (isReadOnly) {
      return (
        <Stack direction={{ xs: 'column', sm: 'row' }} sx={styles.reachBar}>
          <Stack direction="row" sx={styles.reachMetrics}>
            <CalculateOutlined sx={{ color: colors.base.walmart_blue }} />
            {savedReach ? (
              renderReachSummary(savedReach)
            ) : (
              <Typography sx={styles.reachMuted}>
                Reach wasn&apos;t recorded for this request.
              </Typography>
            )}
          </Stack>
        </Stack>
      );
    }

    if (reachError) {
      return (
        <Stack direction={{ xs: 'column', sm: 'row' }} sx={styles.reachBar}>
          <Stack direction="row" sx={styles.reachMetrics}>
            <CalculateOutlined sx={{ color: colors.base.walmart_blue }} />
            <Typography sx={styles.reachMuted}>{reachError}</Typography>
          </Stack>
          <Button
            type="button"
            startIcon={<RefreshOutlined />}
            onClick={calculateReach}
            sx={styles.recalcButton}
          >
            Try again
          </Button>
        </Stack>
      );
    }

    // First state (and while the very first calculation runs): a plain
    // full-width button.
    if (!reach) {
      return (
        <Button
          type="button"
          fullWidth
          disabled={isReachLoading}
          startIcon={<CalculateOutlined />}
          onClick={calculateReach}
          sx={styles.calculateButton}
        >
          {isReachLoading ? 'Calculating...' : 'Calculate my reach'}
        </Button>
      );
    }

    // After a calculation: the summary strip with a Recalculate action.
    return (
      <Stack direction={{ xs: 'column', sm: 'row' }} sx={styles.reachBar}>
        <Stack direction="row" sx={styles.reachMetrics}>
          <CalculateOutlined sx={{ color: colors.base.walmart_blue }} />
          {renderReachSummary(reach, { stale: isReachStale })}
        </Stack>

        <Button
          type="button"
          disabled={isReachLoading}
          startIcon={<RefreshOutlined />}
          onClick={calculateReach}
          sx={styles.recalcButton}
        >
          {isReachLoading ? 'Calculating...' : 'Recalculate'}
        </Button>
      </Stack>
    );
  }

  return (
    <Stack spacing="20px">
      <Box>
        <Typography component="h2" sx={styles.sectionTitle}>
          Targeting{' '}
          <Typography component="span" sx={styles.optionalLabel}>
            optional
          </Typography>
        </Typography>
        <Typography sx={styles.sectionDescription}>
          Choose which stores receive this request
        </Typography>
      </Box>

      {isReadOnly ? (
        <Alert
          icon={<LockOutlined fontSize="small" />}
          severity="info"
          sx={styles.lockedAlert}
        >
          <strong>Targeting is locked.</strong> It can&apos;t be changed after a
          request goes live.
        </Alert>
      ) : (
        <Alert severity="warning" sx={styles.alert}>
          <strong>These filters use AND logic.</strong> A store must match{' '}
          <strong>every</strong> filter below to qualify. Adding filters narrows
          the audience, it never adds more stores.
        </Alert>
      )}

      <Stack>
        {renderFilterRow(
          'store_types',
          'Store type',
          '+ Add type',
          storeTypeOptions
        )}
        {renderFilterRow('states', 'State', '+ Add state', stateOptions)}
        {renderTextRow('regions', 'Regions', 'Add region')}
        {renderTextRow('markets', 'Market number', 'Add market #')}
        {renderTextRow('store_numbers', 'Store number', 'Add store #')}
        {renderUsersRow()}
      </Stack>

      {renderReach()}
    </Stack>
  );
}

TaskTargetingForm.propTypes = {
  initialTargeting: PropTypes.shape({
    store_types: PropTypes.arrayOf(PropTypes.string),
    states: PropTypes.arrayOf(PropTypes.string),
    regions: PropTypes.arrayOf(PropTypes.string),
    markets: PropTypes.arrayOf(PropTypes.string),
    store_numbers: PropTypes.arrayOf(PropTypes.string),
    employee_types: PropTypes.arrayOf(PropTypes.string),
    specific_users: PropTypes.arrayOf(PropTypes.string),
  }),
  onStateChanged: PropTypes.func.isRequired,
  isValidating: PropTypes.bool,
  isReadOnly: PropTypes.bool,
  // The reach snapshot saved at launch, shown read-only for a live task.
  savedReach: PropTypes.shape({
    store_count: PropTypes.number,
    user_count: PropTypes.number,
    total_store_count: PropTypes.number,
  }),
};

TaskTargetingForm.defaultProps = {
  initialTargeting: null,
  isValidating: false,
  isReadOnly: false,
  savedReach: null,
};
